/**
 * Turns a photo or image into a video track so it can be shared like a screen. This is what
 * iPhone and iPad can share: iOS browsers have no screen capture (getDisplayMedia).
 */
export interface ImageShare {
  stream: MediaStream;
  stop(): void;
}

const MAX_W = 1920;
const MAX_H = 1080;

async function decode(file: File): Promise<HTMLImageElement> {
  if (!file.type.startsWith('image/')) throw new Error('El archivo no es una imagen.');
  const url = URL.createObjectURL(file);
  const img = new Image();
  img.src = url;
  try {
    await img.decode();
    return img;
  } catch {
    throw new Error('No se pudo abrir la imagen. Prueba con una foto en JPG o PNG.');
  } finally {
    URL.revokeObjectURL(url);
  }
}

export async function createImageShare(file: File): Promise<ImageShare> {
  const img = await decode(file);
  const scale = Math.min(1, MAX_W / img.naturalWidth, MAX_H / img.naturalHeight);
  // Encoders prefer even dimensions.
  const width = Math.max(2, Math.round((img.naturalWidth * scale) / 2) * 2);
  const height = Math.max(2, Math.round((img.naturalHeight * scale) / 2) * 2);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx || typeof canvas.captureStream !== 'function') throw new Error('Este navegador no puede compartir imágenes.');

  const paint = () => ctx.drawImage(img, 0, 0, width, height);
  paint();
  const stream = canvas.captureStream(5);
  const track = stream.getVideoTracks()[0] as MediaStreamTrack & { requestFrame?: () => void };
  track.contentHint = 'detail';
  // A static canvas emits no new frames; repaint so people who join later (or lose a frame) see it.
  const timer = setInterval(() => {
    paint();
    track.requestFrame?.();
  }, 500);
  return {
    stream,
    stop: () => {
      clearInterval(timer);
      stream.getTracks().forEach((t) => t.stop());
    },
  };
}
